import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi } from "../../../services";
import {
  Appointment,
  MonthlyBalanceData,
  MonthlyBalanceUnpaidAppointment,
  ServiceType,
} from "../../../types";
import { formatDateShort, formatMonthYearLong, formatTime, getTodayMonthYearBrazil } from "../../../utils/dateUtils";
import { Loading, ErrorState, ActionButton } from "../../shared";
import { Icons } from "../../shared/Icons";
import { useCompany } from "../../../contexts/CompanyContext";
import { authService } from "../../../services";
import { UpdatePaymentModal } from "../AdminAppointments/UpdatePaymentModal";
import { BalanceBreakdown } from "./BalanceBreakdown";
import { BalanceByServiceCard } from "./BalanceByServiceCard";
import { BalanceUnpaidRow } from "./BalanceUnpaidRow";
import { formatBalanceCurrency } from "./balanceShared";

interface MonthlyBalanceProps {
  professionalId?: number | null;
  month?: number;
  year?: number;
  onMonthYearChange?: (month: number, year: number) => void;
}

function parseUnpaidPrice(price: number | string): number {
  if (typeof price === "number") return price;
  return parseFloat(price) || 0;
}

function unpaidPriceString(price: number | string): string {
  if (typeof price === "number") return String(price);
  return price;
}

function mapMonthlyUnpaidToAppointment(a: MonthlyBalanceUnpaidAppointment): Appointment {
  const priceStr = unpaidPriceString(a.price);
  return {
    id: a.id,
    clientId: a.client.id,
    client: a.client,
    professionalId: a.professionalId ?? 0,
    serviceId: a.serviceId,
    appointmentDate: a.appointmentDate,
    startTime: a.startTime ?? "",
    endTime: a.endTime ?? "",
    price: priceStr,
    status: a.status,
    payment: null,
    isRecurrency: false,
    updatedAt: "",
    createdAt: "",
    service: {
      name: a.service?.name ?? "Serviço",
      description: a.service?.description ?? "",
      duration: 0,
      price: priceStr,
      type: ServiceType.TIME,
      companyId: 0,
    },
    progressSteps: [],
  };
}

export const MonthlyBalance: React.FC<MonthlyBalanceProps> = ({
  professionalId: propProfessionalId,
  month: propMonth,
  year: propYear,
  onMonthYearChange,
}) => {
  const navigate = useNavigate();
  const { company } = useCompany();
  const userData = authService.getUserData();
  const userType = userData?.userType;
  const isProfessional = userType === "professional";
  const loggedInProfessionalId = isProfessional ? userData?.id : null;

  const [monthYear, setMonthYear] = useState(() => {
    const today = getTodayMonthYearBrazil();
    return {
      month: propMonth ?? today.month,
      year: propYear ?? today.year,
    };
  });
  const { month, year } = monthYear;
  const [payload, setPayload] = useState<MonthlyBalanceData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [appointmentForPayment, setAppointmentForPayment] = useState<Appointment | null>(null);

  const professionalId = isProfessional ? loggedInProfessionalId : propProfessionalId;

  useEffect(() => {
    if (propMonth != null && propYear != null) {
      setMonthYear({ month: propMonth, year: propYear });
    }
  }, [propMonth, propYear]);

  const goPrevMonth = () => {
    setMonthYear(({ month: m, year: y }) => {
      const next = m <= 1 ? { month: 12, year: y - 1 } : { month: m - 1, year: y };
      onMonthYearChange?.(next.month, next.year);
      return next;
    });
  };

  const goNextMonth = () => {
    setMonthYear(({ month: m, year: y }) => {
      const next = m >= 12 ? { month: 1, year: y + 1 } : { month: m + 1, year: y };
      onMonthYearChange?.(next.month, next.year);
      return next;
    });
  };

  const handleViewAppointments = () => {
    const firstDay = `${year}-${String(month).padStart(2, "0")}-01`;
    sessionStorage.setItem("adminAppointmentsSelectedDate", firstDay);
    navigate("/admin/appointments");
  };

  const fetchMonthlyBalance = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const professionalIdToUse = company?.category !== "car" ? (professionalId ?? undefined) : undefined;
      const response = await adminApi.getMonthlyBalance(month, year, professionalIdToUse);

      if (response.success && response.data) {
        setPayload(response.data);
      } else {
        setError(response.error || "Erro ao carregar saldo mensal");
        setPayload(null);
      }
    } catch (err) {
      console.error("Error fetching monthly balance:", err);
      setError("Erro ao carregar saldo mensal");
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [company?.category, month, year, professionalId]);

  useEffect(() => {
    if (company) {
      fetchMonthlyBalance();
    }
  }, [company, fetchMonthlyBalance]);

  const balance = payload;
  const appointmentsWithoutPayment = balance?.appointmentsWithoutPayment ?? [];

  return (
    <>
      <div className="bg-white dark:!bg-slate-800 rounded-xl sm:rounded-2xl shadow-medium border border-gray-100 dark:border-slate-700 p-3 sm:p-4 md:p-6">
      <div className="mb-4 sm:mb-6">
        <h3 className="text-sm sm:text-base md:text-lg font-semibold text-gray-800 dark:text-slate-100 mb-3 sm:mb-4">
          Saldo Mensal
        </h3>

        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-stretch sm:items-center">
          <div className="flex items-center justify-center gap-2 sm:gap-3 flex-1 min-w-0">
            <button
              type="button"
              onClick={goPrevMonth}
              className="flex-shrink-0 p-2 rounded-lg border border-gray-200 dark:border-slate-600 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
              aria-label="Mês anterior"
            >
              <Icons.ChevronLeft sx={{ fontSize: 24 }} />
            </button>
            <span className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white text-center flex-1 min-w-0 truncate px-1">
              {formatMonthYearLong(month, year)}
            </span>
            <button
              type="button"
              onClick={goNextMonth}
              className="flex-shrink-0 p-2 rounded-lg border border-gray-200 dark:border-slate-600 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
              aria-label="Próximo mês"
            >
              <Icons.ChevronRight sx={{ fontSize: 24 }} />
            </button>
          </div>
          <div className="flex justify-end w-full sm:w-auto sm:ml-auto">
            <ActionButton
              variant="primary"
              size="md"
              icon={<Icons.Calendar sx={{ fontSize: 20 }} />}
              onClick={handleViewAppointments}
              className="w-full sm:w-auto"
            >
              Ver Agendamentos
            </ActionButton>
          </div>
        </div>
      </div>

      {loading ? (
        <Loading />
      ) : error ? (
        <ErrorState message={error} />
      ) : balance ? (
        <BalanceBreakdown
          balance={balance}
          totalPrimaryLabel="Total do Mês"
          emptyPeriodMessage="Nenhum pagamento registrado para este mês"
          unpaidCount={appointmentsWithoutPayment.length}
          unpaidRows={appointmentsWithoutPayment.map((row) => (
            <BalanceUnpaidRow
              key={row.id}
              onPay={() => {
                setAppointmentForPayment(mapMonthlyUnpaidToAppointment(row));
                setPaymentModalOpen(true);
              }}
            >
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-white">
                  {row.client?.name || "Cliente"}
                </span>
                <span className="text-xs text-gray-600 dark:text-slate-400">{formatDateShort(row.appointmentDate)}</span>
                {row.startTime ? (
                  <span className="text-xs text-gray-600 dark:text-slate-400">{formatTime(row.startTime)}</span>
                ) : null}
              </div>
              <p className="text-xs text-gray-600 dark:text-slate-400 truncate">{row.service?.name || "Serviço"}</p>
              <p className="text-xs sm:text-sm font-semibold text-red-700 dark:text-red-400 mt-1">
                {formatBalanceCurrency(parseUnpaidPrice(row.price))}
              </p>
            </BalanceUnpaidRow>
          ))}
          showAllPaidMessage={appointmentsWithoutPayment.length === 0 && balance.total > 0}
        />
      ) : null}

      {appointmentForPayment && (
        <UpdatePaymentModal
          isOpen={paymentModalOpen}
          onClose={() => {
            setPaymentModalOpen(false);
            setAppointmentForPayment(null);
          }}
          onSuccess={(updatedAppointment) => {
            if (updatedAppointment) {
              fetchMonthlyBalance();
            }
            setPaymentModalOpen(false);
            setAppointmentForPayment(null);
          }}
          appointment={appointmentForPayment}
        />
      )}
      </div>
      {!loading && !error && balance ? (
        <BalanceByServiceCard items={balance.byService ?? []} />
      ) : null}
    </>
  );
};
